import { useState } from "react";
import styled, { css } from "styled-components";
import useTranslations from "../hooks/useTranslations";

import { Input, DeductionHolder, Minus } from "./Parts";
import { useEffect } from "react";
import { Fragment } from "react";
import { useSelector } from "react-redux";
import { useMemo } from "react";

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -2px;
  margin-top: 10px;
`;

const Item = styled.div`
  flex: 0 0 33.33%;
  padding: 2px;
`;

const Button = styled.button`
  height: 60px;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  transition: 0.1s ease-out transform, 0.1s ease-out background-color;
  border: solid 2px rgba(51, 51, 51, 0.1);
  border-radius: 4px;
  font-size: 28px;
  background: #fff;
  outline: none !important;
  &:active {
    transform: scale(0.96);
    background: rgba(51, 51, 51, 0.05);
  }
  > svg {
    width: 40px;
  }
`;

const TabsGrid = styled.div`
  display: flex;
  justify-content: space-between;
  > * {
    width: calc(50% - 2px);
  }
`;

const TabButton = styled(Button)`
  font-size: 24px;
  margin-top: 10px;
  ${({ $active }) =>
    $active &&
    css`
      border-color: #6e41e2;
    `}
`;

const EButton = styled.button`
  transition: 0.1s ease-out transform, 0.1s ease-out background-color;
  width: 100%;
  border: solid 2px rgba(51, 51, 51, 0.1);
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  outline: none !important;
  padding: 10px 0;
  ${({ $active }) =>
    $active &&
    css`
      border-color: #6e41e2;
    `}
  ${({ $invalid }) =>
    $invalid &&
    css`
      color: red;
    `}
`;

const EButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  > div {
    padding: 2px;
    flex: 0 0 25%;

    &:nth-of-type(9) {
      flex: 0 0 33.33%;
    }
    &:nth-of-type(10) {
      flex: 0 0 33.33%;
      ${EButton} {
        padding-top: 13px;
        border: 0;
        font-weight: bold;
      }
    }
    &:nth-of-type(11) {
      flex: 0 0 33.33%;
    }
  }
`;

const buttons = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const dScoreButtons = [
  "0",
  "0.1",
  "0.2",
  "0.3",
  "0.4",
  "0.5",
  "0.6",
  "0.7",
  "0.8",
  "0.9",
  "1.0",
];

const Keyboard = ({
  value,
  setValue,
  deduction,
  setDeduction,
  hasDeductionInput,
  hasScoreInput,
  userPosition,
  setValueMeta,
  valueMeta,
  competitorId,
}) => {
  const translations = useTranslations();

  const notification = useSelector((rootState) => rootState.notification);

  const invalidDValues = useMemo(() => {
    if (notification?.[0] === "[") {
      return JSON.parse(notification);
    }

    return [];
  }, [notification]);

  const [dValues, setDValues] = useState(
    valueMeta ?? {
      E1: "",
      E2: "",
      E3: "",
      E4: "",
      E5: "",
      E6: "",
      E7: "",
      E8: "",
      Combi: "",
      Deduction: "",
    }
  );

  // alert(JSON.stringify(dValues));

  const [activeTab, setActiveTab] = useState(() => {
    if (userPosition === "D") {
      return "E1";
    }

    return hasScoreInput ? 1 : 2;
  });

  const setter = activeTab === 1 ? setValue : setDeduction;

  const currentValue = activeTab === 1 ? value : deduction;

  const onDelete = () => {
    if (userPosition === "D") {
      setDValues((prev) => {
        return { ...prev, [activeTab]: "" };
      });

      return;
    }

    setter((prevValue) => prevValue.slice(0, -1));
  };

  const onValueClick = (val) => {
    if (userPosition === "D") {
      setDValues((prev) => {
        if (activeTab !== "Deduction") {
          return { ...prev, [activeTab]: val };
        }

        const prevValue = prev[activeTab];

        if (prevValue.length === 3 && prevValue[0] !== "0") {
          return prev;
        }

        if (!prevValue) {
          return { ...prev, [activeTab]: `0.${val}` };
        }

        if (activeTab === "Deduction") {
          return { ...prev, [activeTab]: `${prevValue[2]}.${val}` };
        }

        if (val === "0" && prevValue === "0.1") {
          return { ...prev, [activeTab]: "1.0" };
        }

        return prev;
      });

      if (activeTab !== "Deduction") {
        const keys = Object.keys(dValues);

        setActiveTab(keys[keys.indexOf(activeTab) + 1]);
      }

      return;
    }

    setter((prevValue) => `${prevValue}${val}`);
  };

  const dotDisabled = currentValue === "" || currentValue.includes(".");

  const isAOnDeduction = userPosition === "A" && activeTab === 2;
  const halfStepDisabled = (digit) => {
    if (!isAOnDeduction) return false;
    const dotIdx = currentValue.indexOf(".");
    if (dotIdx === -1) return false;
    const afterDot = currentValue.slice(dotIdx + 1);
    if (afterDot.length >= 1) return true;
    return digit !== "0" && digit !== "5";
  };

  const renderInputs = () => {
    if (userPosition === "D") {
      return (
        <EButtons>
          {Object.keys(dValues).map((variant) => (
            <Fragment key={variant}>
              {variant === "Deduction" && (
                <div>
                  <EButton>Score: {value}</EButton>
                </div>
              )}
              <div className="e-tabs-grid-btn">
                <EButton
                  $invalid={invalidDValues.includes(variant)}
                  $active={activeTab === variant}
                  onClick={() => setActiveTab(variant)}
                >
                  {variant}: {dValues[variant] || "0._"}
                </EButton>
              </div>
            </Fragment>
          ))}
        </EButtons>
      );
    }

    if (hasDeductionInput && hasScoreInput) {
      return (
        <TabsGrid>
          <Input
            value={value}
            type="text"
            placeholder={translations["Įveskite balą"]}
            readOnly
          />
          <DeductionHolder>
            <Minus>-</Minus>
            <Input
              value={deduction}
              type="text"
              placeholder={translations["Įveskite baudą"]}
              readOnly
            />
          </DeductionHolder>
        </TabsGrid>
        // <Input
        //   value={value}
        //   type="text"
        //   placeholder={translations["Įveskite balą"]}
        //   readOnly
        // />
      );
    }
    if (hasDeductionInput) {
      return (
        <DeductionHolder>
          <Minus>-</Minus>
          <Input
            value={deduction}
            type="text"
            placeholder={translations["Įveskite baudą"]}
            readOnly
          />
        </DeductionHolder>
      );
    }

    if (userPosition === "E") {
      return (
        <DeductionHolder>
          <Minus>-</Minus>
          <Input
            value={value}
            type="text"
            placeholder={translations["Įveskite balą"]}
            readOnly
          />
        </DeductionHolder>
      );
    }

    return (
      <Input
        value={value}
        type="text"
        placeholder={translations["Įveskite balą"]}
        readOnly
      />
    );
  };

  useEffect(() => {
    if (userPosition !== "D") {
      return;
    }

    const value = Object.keys(dValues)
      .reduce((acc, curr) => {
        if (curr === "Deduction") {
          return acc;
        }

        return acc + parseFloat(dValues[curr] || "0");
      }, 0)
      .toFixed(1);

    setValue(value);
    setDeduction(dValues.Deduction ?? "0");
    setValueMeta(dValues);
  }, [userPosition, dValues]);

  useEffect(() => {
    if (userPosition !== "D") {
      return;
    }

    setActiveTab("E1");
    setDValues({
      E1: "",
      E2: "",
      E3: "",
      E4: "",
      E5: "",
      E6: "",
      E7: "",
      E8: "",
      Combi: "",
      Deduction: "",
    });
  }, [competitorId]);

  return (
    <>
      {renderInputs()}
      {hasDeductionInput && hasScoreInput && userPosition !== "D" && (
        <>
          <TabsGrid>
            <TabButton
              $active={activeTab === 1}
              onClick={() => setActiveTab(1)}
            >
              {translations["Balas"]}
            </TabButton>
            <TabButton
              $active={activeTab === 2}
              onClick={() => setActiveTab(2)}
            >
              {translations["Bauda"]}
            </TabButton>
          </TabsGrid>
        </>
      )}
      {userPosition === "D" && activeTab !== "Deduction" ? (
        <Grid>
          {dScoreButtons.map((val) => (
            <Item key={val}>
              <Button onClick={() => onValueClick(val)}>{val}</Button>
            </Item>
          ))}
          <Item>
            <Button onClick={onDelete}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="black"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
                />
              </svg>
            </Button>
          </Item>
        </Grid>
      ) : (
        <Grid>
          {buttons.map((btn) => (
            <Item key={btn}>
              <Button
                onClick={() => onValueClick(btn.toString())}
                disabled={halfStepDisabled(btn.toString())}
              >
                {btn}
              </Button>
            </Item>
          ))}
          <Item>
            <Button onClick={() => onValueClick(".")} disabled={dotDisabled}>
              .
            </Button>
          </Item>
          <Item>
            <Button
              onClick={() => onValueClick("0")}
              disabled={halfStepDisabled("0")}
            >
              0
            </Button>
          </Item>
          <Item>
            <Button
              onClick={onDelete}
              disabled={currentValue === "" && userPosition !== "D"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="black"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
                />
              </svg>
            </Button>
          </Item>
        </Grid>
      )}
    </>
  );
};

export default Keyboard;
